import { Alert, Badge, Card, Stack, Table, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAssignments, useNames } from '../modules/risq/data';
import { PageTitle, QueryState, usePositionNames } from '../ui/components';
import { formatDate } from '../ui/format';
import { useWorkplace } from './access';

/**
 * D8 Crew & access (read part): position → person, since; assignment history. Assigning
 * (invite) and revoking use `invite-member` and `revoke_assignment` from Task 5.
 */
export function CrewPage() {
  const { t } = useTranslation('dashboard');
  const { vesselId = '' } = useParams();
  const { vessels, access } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const assignments = useAssignments(vessel === undefined ? [] : [vessel.id]);
  const names = useNames(vesselId);
  const { name } = usePositionNames();
  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  const rows = assignments.data?.get(vessel.id) ?? [];
  const active = rows.filter((a) => a.revokedAt === null);
  const person = (id: string) => names.data?.get(id) ?? t('activity.someone');
  return (
    <>
      <PageTitle
        title={t('crew.title', { vessel: vessel.name })}
        back={{ to: `/vessels/${vessel.id}`, label: vessel.name }}
      />
      <Stack gap="lg">
        <Alert color="blue">{t('crew.task5')}</Alert>
        <QueryState loading={assignments.isPending} error={assignments.error}>
          {() => (
            <>
              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('crew.onBoard')}
                  </Title>
                  <Table>
                    <Table.Tbody>
                      {access.positions.map((p) => {
                        const holder = active.find((a) => a.positionCode === p.code);
                        return (
                          <Table.Tr key={p.code}>
                            <Table.Td>{name(p.code)}</Table.Td>
                            <Table.Td>
                              {holder === undefined ? (
                                <Badge color="gray" variant="outline" tt="none">
                                  {t('crew.nobody')}
                                </Badge>
                              ) : (
                                <Text size="sm">
                                  {t('crew.since', {
                                    name: person(holder.userId),
                                    date: formatDate(holder.startsAt),
                                  })}
                                </Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>
              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('crew.history')}
                  </Title>
                  <Table>
                    <Table.Tbody>
                      {rows.map((a) => (
                        <Table.Tr key={a.id}>
                          <Table.Td>{name(a.positionCode)}</Table.Td>
                          <Table.Td>{person(a.userId)}</Table.Td>
                          <Table.Td>
                            {a.revokedAt === null
                              ? t('crew.activeSince', { date: formatDate(a.startsAt) })
                              : t('crew.ended', {
                                  from: formatDate(a.startsAt),
                                  to: formatDate(a.revokedAt),
                                  reason: a.revokeReason ?? t('crew.noReason'),
                                })}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>
            </>
          )}
        </QueryState>
      </Stack>
    </>
  );
}
